/**
 * Project Detail → Laminate tab.
 *
 * 1:1 with the design folder's `project-details.html#ptab-laminate`
 * (lines 1423-1564) and 100% schema parity with mobile
 * `src/features/projects/tabs/LaminateTab.tsx`.
 *
 * Layout:
 *   .lam-layout
 *     LEFT  .lam-main         — per-room blocks, each with header
 *                                (small caps room name + "N items"
 *                                pill) and a stack of .lam-row cards
 *                                (48px thumbnail + brand + mono code
 *                                meta + notes + caret).
 *     RIGHT .lam-right-panel  — Overview card (Rooms · Laminates ·
 *                                Brands) + Distribution card (per-
 *                                room bar chart with iconified row).
 *   Hidden on <900px; the rail collapses to keep the row list
 *   readable on phones.
 *
 * Click a row → opens the form slide-over in edit mode.
 * "+ Add laminate" header CTA → opens it in create mode.
 *
 * Capability gate: only `canEdit` users (owner / superAdmin /
 * admin / manager / siteEngineer / designer) see the Add CTA and
 * can edit / delete; the Firestore rule denies writes anyway, this
 * just hides the affordance to avoid surfacing permission errors.
 */
import { useMemo, useState } from 'react';

import { LaminateFormSlideOver } from '@/components/LaminateFormSlideOver';
import {
  roomIcon,
  type Laminate,
  type RoomLaminates,
} from '@/lib/laminates';
import { useFullOrganization } from '@/lib/useFullOrganization';
import { useLaminates } from '@/lib/useLaminates';
import type { ProjectListItem } from '@/lib/useProjects';

export function LaminateTab({ project }: { project: ProjectListItem }) {
  const { org } = useFullOrganization();
  const orgId = project.orgId ?? org?.id ?? null;

  const { data, rooms, brands, loading, canEdit } = useLaminates(
    project.id,
    orgId ?? undefined,
  );

  const [formMode, setFormMode] = useState<
    | { kind: 'closed' }
    | { kind: 'create'; defaultRoomName?: string }
    | { kind: 'edit'; item: Laminate }
  >({ kind: 'closed' });

  // Largest room count drives the bar widths in the distribution card.
  const maxRoomCount = useMemo(
    () => rooms.reduce((m, r) => Math.max(m, r.laminates.length), 0),
    [rooms],
  );

  const formItem = formMode.kind === 'edit' ? formMode.item : null;
  const defaultRoom =
    formMode.kind === 'create' ? formMode.defaultRoomName : undefined;

  return (
    <div>
      {/* Header — title + count + Add CTA */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 18,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--text-secondary)',
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          Laminates · {data.length}
        </h3>
        {canEdit ? (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setFormMode({ kind: 'create' })}
            disabled={!orgId}
            style={{ padding: '6px 16px', borderRadius: 20, fontSize: 12 }}
          >
            <i className="ph-bold ph-plus" /> ADD LAMINATE
          </button>
        ) : null}
      </div>

      {/* 2-col layout — main list + insights rail */}
      <div className="lam-layout">
        {/* LEFT — room blocks */}
        <div className="lam-main">
          {loading && data.length === 0 ? (
            <div
              className="card"
              style={{ padding: 48, textAlign: 'center' }}
            >
              Loading laminates…
            </div>
          ) : rooms.length === 0 ? (
            <div
              className="card"
              style={{ padding: 48, textAlign: 'center' }}
            >
              <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>
                <i className="ph ph-stack" />
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
                No laminates added
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                  maxWidth: 420,
                  margin: '0 auto 16px',
                }}
              >
                {canEdit
                  ? 'Add laminate selections for each room — brand, finish, edge band, and a reference swatch photo.'
                  : 'The studio will publish laminate selections here when they’re ready for your review.'}
              </div>
              {canEdit ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setFormMode({ kind: 'create' })}
                  disabled={!orgId}
                >
                  <i className="ph-bold ph-plus" /> Add your first laminate
                </button>
              ) : null}
            </div>
          ) : (
            rooms.map((room) => (
              <RoomBlock
                key={room.roomName}
                room={room}
                canEdit={canEdit}
                onClickRow={(item) => setFormMode({ kind: 'edit', item })}
                onAddInRoom={
                  canEdit
                    ? () =>
                        setFormMode({
                          kind: 'create',
                          defaultRoomName: room.roomName,
                        })
                    : undefined
                }
              />
            ))
          )}
        </div>

        {/* RIGHT — insights rail */}
        <div className="lam-right-panel">
          {/* Overview */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              className="card-header"
              style={{
                background: '#F9FAFB',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div className="card-title" style={{ fontSize: 12 }}>
                LAMINATE OVERVIEW
              </div>
            </div>
            <div style={{ padding: 16, display: 'flex', gap: 12 }}>
              <OverviewCell label="ROOMS" value={rooms.length} />
              <div style={{ width: 1, background: 'var(--border)' }} />
              <OverviewCell label="LAMINATES" value={data.length} />
              <div style={{ width: 1, background: 'var(--border)' }} />
              <OverviewCell label="BRANDS" value={brands.length} />
            </div>
          </div>

          {/* Distribution */}
          {rooms.length > 0 ? (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div
                className="card-header"
                style={{
                  background: '#F9FAFB',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div className="card-title" style={{ fontSize: 12 }}>
                  LAMINATE DISTRIBUTION
                </div>
              </div>
              <div
                style={{
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {rooms.map((r) => {
                  const pct =
                    maxRoomCount > 0
                      ? Math.round((r.laminates.length / maxRoomCount) * 100)
                      : 0;
                  return (
                    <div key={r.roomName} className="lam-dist-row">
                      <div className="lam-dist-icon">
                        <i className={roomIcon(r.roomName)} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 4,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {r.roomName}
                          </span>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              marginLeft: 8,
                            }}
                          >
                            {r.laminates.length}
                          </span>
                        </div>
                        <div className="lam-dist-bar">
                          <div
                            className="lam-dist-bar-fill"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Brands list — collapsible chip cloud */}
          {brands.length > 0 ? (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div
                className="card-header"
                style={{
                  background: '#F9FAFB',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div className="card-title" style={{ fontSize: 12 }}>
                  BRANDS IN USE
                </div>
              </div>
              <div
                style={{
                  padding: 12,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                }}
              >
                {brands.map((b) => (
                  <span
                    key={b}
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      background: '#F3F4F6',
                      padding: '4px 10px',
                      borderRadius: 999,
                    }}
                  >
                    {b}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Form */}
      <LaminateFormSlideOver
        open={formMode.kind !== 'closed'}
        onClose={() => setFormMode({ kind: 'closed' })}
        orgId={orgId}
        projectId={project.id}
        item={formItem}
        existingRooms={rooms.map((r) => r.roomName)}
        defaultRoomName={defaultRoom}
      />
    </div>
  );
}

// ───── Room block ─────

function RoomBlock({
  room,
  canEdit,
  onClickRow,
  onAddInRoom,
}: {
  room: RoomLaminates;
  canEdit: boolean;
  onClickRow: (item: Laminate) => void;
  onAddInRoom?: () => void;
}) {
  return (
    <div className="lam-room-block">
      <div className="lam-room-header">
        <h3>{room.roomName}</h3>
        <div className="lam-room-pill">
          {room.laminates.length}{' '}
          {room.laminates.length === 1 ? 'item' : 'items'}
        </div>
        {canEdit && onAddInRoom ? (
          <button
            type="button"
            onClick={onAddInRoom}
            title={`Add laminate in ${room.roomName}`}
            style={{
              marginLeft: 'auto',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              padding: '3px 10px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <i className="ph ph-plus" /> Add
          </button>
        ) : null}
      </div>

      <div>
        {room.laminates.map((l) => (
          <LaminateRow
            key={l.id}
            item={l}
            onClick={canEdit ? () => onClickRow(l) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

// ───── Row ─────

function LaminateRow({
  item,
  onClick,
}: {
  item: Laminate;
  onClick?: () => void;
}) {
  const clickable = !!onClick;
  // Same mono meta string the mobile renders: laminate code · finish ·
  // edge band — only joining non-empty parts so an entry with just a
  // finish doesn't look like "· MATTE ·".
  const meta = [
    item.laminateCode,
    item.finish,
    item.edgeBandCode ? `EB ${item.edgeBandCode}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
    .toUpperCase();

  return (
    <div
      className="card lam-row"
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick!();
              }
            }
          : undefined
      }
      title={clickable ? `Edit ${item.brand}` : item.brand}
      style={{ cursor: clickable ? 'pointer' : 'default' }}
    >
      {item.photoUrl ? (
        <div
          className="lam-thumb"
          style={{ backgroundImage: `url(${item.photoUrl})` }}
        />
      ) : (
        <div className="lam-thumb-empty">
          <i className="ph ph-image" />
        </div>
      )}

      <div className="lam-body">
        <div className="lam-brand">{item.brand}</div>
        {meta ? <div className="lam-meta">{meta}</div> : null}
        {item.notes ? <div className="lam-notes">{item.notes}</div> : null}
      </div>

      <i className="ph ph-caret-right lam-chev" />
    </div>
  );
}

// ───── Overview cell ─────

function OverviewCell({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          color: 'var(--text-primary)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          marginTop: 2,
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>
    </div>
  );
}
